import type {
  LinhaEstrutura,
  SituacaoBase,
  TipoObraBase,
} from "@/modules/financeiro-configuracoes";
import { BASE_DADOS_SEM_VALOR } from "./base-dados.constants";

export type AlvoSituacao =
  | { tipo: "tipo-obra"; tipoObraId: string; codigo: string; nome: string }
  | {
      tipo: "grupo";
      grupoId: string;
      nome: string;
      tipos: TipoDoAlvo[];
      comSubgrupos: boolean;
    }
  | {
      tipo: "subgrupo";
      grupoId: string;
      subgrupoId: string;
      nome: string;
      grupoNome: string;
      tipoObra: TipoDoAlvo;
      irmaos: string[];
    };

export interface TipoDoAlvo {
  codigo: string;
  nome: string;
}

export interface AcaoBase {
  tipo: "inativar" | "ativar";
  alvo: AlvoSituacao;
  nome: string;
}

export type AcaoInativar = AcaoBase & { tipo: "inativar" };

export type ChaveOrdem = string | number | null;

export interface LinhaBase {
  id: string;
  situacao: SituacaoBase;
  celulas: Record<string, string>;
  chaves: Record<string, ChaveOrdem>;
  acao: AcaoBase;
}

export interface Ordenacao {
  campo: string;
  direcao: "asc" | "desc";
}

const acaoPara = (situacao: SituacaoBase, alvo: AlvoSituacao): AcaoBase => ({
  tipo: situacao === "ATIVO" ? "inativar" : "ativar",
  alvo,
  nome: alvo.nome,
});

const tiposDoGrupo = (itens: LinhaEstrutura[], grupoId: string): TipoDoAlvo[] => {
  const tipos = new Map<string, TipoDoAlvo>();
  for (const item of itens) {
    if (item.grupoId === grupoId) {
      tipos.set(item.tipoObraId, { codigo: item.tipoObraCodigo, nome: item.tipoObraNome });
    }
  }
  return [...tipos.values()];
};

const rotuloTipo = (codigo: string, nome: string) => `${codigo} · ${nome}`;

export function montarLinhasEstrutura(itens: LinhaEstrutura[]): LinhaBase[] {
  return itens.map((item) => {
    const tipoObra = rotuloTipo(item.tipoObraCodigo, item.tipoObraNome);
    const alvoGrupo: AlvoSituacao = {
      tipo: "grupo",
      grupoId: item.grupoId,
      nome: item.grupoNome,
      tipos: tiposDoGrupo(itens, item.grupoId),
      comSubgrupos: item.subgrupoId !== null,
    };
    const alvoSubgrupo: AlvoSituacao | null = item.subgrupoId
      ? {
          tipo: "subgrupo",
          grupoId: item.grupoId,
          subgrupoId: item.subgrupoId,
          nome: item.subgrupoNome ?? "",
          grupoNome: item.grupoNome,
          tipoObra: { codigo: item.tipoObraCodigo, nome: item.tipoObraNome },
          irmaos: itens
            .filter(
              (outro) => outro.grupoId === item.grupoId && outro.tipoObraId === item.tipoObraId,
            )
            .flatMap((outro) => (outro.subgrupoNome ? [outro.subgrupoNome] : [])),
        }
      : null;
    const alvo = alvoSubgrupo && item.grupoSituacao !== "INATIVO" ? alvoSubgrupo : alvoGrupo;

    return {
      id: `${item.tipoObraId}:${item.grupoId}:${item.subgrupoId ?? "-"}`,
      situacao: item.situacao,
      celulas: {
        tipoObra,
        grupo: item.grupoNome,
        subgrupo: item.subgrupoNome ?? BASE_DADOS_SEM_VALOR,
        escopos: String(item.quantidadeEscoposMacro),
      },
      chaves: {
        tipoObra,
        grupo: item.grupoNome,
        subgrupo: item.subgrupoNome,
        escopos: item.quantidadeEscoposMacro,
        situacao: item.situacao,
      },
      acao: acaoPara(item.situacao, alvo),
    };
  });
}

export function montarLinhasTiposObra(tipos: TipoObraBase[]): LinhaBase[] {
  return tipos.map((tipo) => ({
    id: tipo.id,
    situacao: tipo.situacao,
    celulas: { codigo: tipo.codigo, nome: tipo.nome, grupos: String(tipo.quantidadeGrupos) },
    chaves: {
      codigo: tipo.codigo,
      nome: tipo.nome,
      grupos: tipo.quantidadeGrupos,
      situacao: tipo.situacao,
    },
    acao: acaoPara(tipo.situacao, {
      tipo: "tipo-obra",
      tipoObraId: tipo.id,
      codigo: tipo.codigo,
      nome: tipo.nome,
    }),
  }));
}

export function filtrarInativos(linhas: LinhaBase[], mostrarInativos: boolean): LinhaBase[] {
  return mostrarInativos ? linhas : linhas.filter((linha) => linha.situacao === "ATIVO");
}

function comparar(a: ChaveOrdem, b: ChaveOrdem): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "pt-BR", { numeric: true, sensitivity: "base" });
}

export function ordenarLinhas(linhas: LinhaBase[], ordenacao: Ordenacao | null): LinhaBase[] {
  if (!ordenacao) return linhas;
  const sinal = ordenacao.direcao === "asc" ? 1 : -1;

  return [...linhas].sort((a, b) => {
    const chaveA = a.chaves[ordenacao.campo] ?? null;
    const chaveB = b.chaves[ordenacao.campo] ?? null;
    if (chaveA === null || chaveB === null) {
      return chaveA === chaveB ? 0 : chaveA === null ? 1 : -1;
    }
    return sinal * comparar(chaveA, chaveB);
  });
}

export function proximaOrdenacao(atual: Ordenacao | null, campo: string): Ordenacao | null {
  if (atual?.campo !== campo) return { campo, direcao: "asc" };
  return atual.direcao === "asc" ? { campo, direcao: "desc" } : null;
}
